import { useEffect } from 'react'
import { Button } from '@/app/common/components/button/Button'
import Label from '@/common/components/Label.tsx'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import Textarea from '@/common/components/Textarea.tsx'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog.tsx'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select.tsx'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { useSelector } from 'react-redux'
import { useSubmitPaymentDecision } from '@/apis/payment_tasks/useSubmitPaymentDecision'
import { ReasoningInputSchema } from '@/app/apis/payment_tasks/ReasoningInputSchema'
import { DecisionSchema } from '@/app/apis/payment_tasks/DecisionSchema'
import { RootState } from '@/storage/store'
import { toast } from '@/shad/ui/toast/use-toast'
import { InfoCircleIcon } from '@/common/icons/general'
import { parseRequestError } from '@/app/common/utilities'

const FormSchema = z.object({
  decision: DecisionSchema,
  reasoning: ReasoningInputSchema,
  justification: z.string().min(1, { message: 'Internal note is required' }),
  public_reply: z.string().nullable().optional(),
})

export type DecisionFormData = z.infer<typeof FormSchema>

interface Props {
  payment_id?: number | null
  refetch?: () => void
}

interface Macros {
  title: string
  body: string
  code: string
}

const macros: Array<Macros> = [
  {
    title: 'Accept Messaging',
    body: 'Thank you for submitting the required documentation. We have reviewed your submission and are pleased to inform you that your updates have been accepted. Please log in to your account to proceed with your application.',
    code: 'accepted',
  },
  {
    title: 'Reject Messaging',
    body: 'We regret to inform you that we are unable to proceed with your application. The document you have provided does not meet the guidelines.\nIf you have alternate documentation to support your application, please access your homepage and upload the additional documents for review.',
    code: 'rejected',
  },
  { title: 'Blank Message', body: '', code: '' },
]

function PaymentReviewPopup({ payment_id, refetch }: Props) {
  const [open, setOpen] = useState(false)
  const [selectedMacro, setSelectedMacro] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    control,
    watch,
  } = useForm<DecisionFormData>({
    resolver: zodResolver(FormSchema),
  })

  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const { mutate: submitDecision, status: submitFlagStatus } =
    useSubmitPaymentDecision({
      onError: (error) => {
        const parsedErorr = parseRequestError(error).message
        toast({
          title: 'Error',
          variant: 'destructive',
          description: parsedErorr,
        })
      },
      onSuccess: () => {
        if (refetch) {
          refetch()
        }
        handleClose()
        toast({
          title: `Payment was succesfully marked as ${getValues().decision}`,
          description: 'The payment was successfully updated',
        })
      },
    })

  const onSubmit = (data: DecisionFormData) => {
    submitDecision({
      program_id: selectedProgramYear?.id ?? 0,
      paymentId: payment_id ?? 0,
      decision: data.decision,
      justification: data.justification,
      reasoning: data.reasoning,
      public_reply: data.public_reply ?? null,
    })
  }

  const handleClose = () => {
    setOpen(false)
  }

  watch('reasoning')

  const handleOpenChange = async (open: boolean) => {
    setOpen(open)
  }

  const decision = useWatch({ control, name: 'decision' })
  useEffect(() => {
    const macro = macros.find((macro) => macro.code == decision)
    if (macro) {
      setPublicReply(macro)
      setSelectedMacro(macro.title)
    }
  }, [decision])

  const setPublicReply = (macro: Macros) => {
    setValue('public_reply', macro.body)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="primary" size="sm">
          Review
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'> */}
          {/* <div className='space-y-4'> */}
          <div className="space-y-2">
            <Label>Decision</Label>
            <Controller
              name="decision"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="accepted" id="accept" />
                    <Label weight="normal" htmlFor="accept">
                      I <span className="text-green-700">accept</span> the
                      notification made by the applicant
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="rejected" id="reject" />
                    <Label weight="normal" htmlFor="reject">
                      I <span className="text-red-700">reject</span> the
                      notification made by the applicant
                    </Label>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.decision && (
              <p className="text-sm text-red-500">{errors.decision.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label>Reasoning</Label>
            <Select
              value={getValues().reasoning}
              onValueChange={(value) => {
                const reasoning = ReasoningInputSchema.parse(value)
                setValue('reasoning', reasoning)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select reasoning" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={'Based on evidence'}>
                  Based on evidence
                </SelectItem>
                <SelectItem value={'Based on program rules'}>
                  Based on program rules
                </SelectItem>
                <SelectItem value={'Other'}>Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.reasoning && (
              <p className="text-sm text-red-500">{errors.reasoning.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label>
              Internal Note
              <span className="text-danger ml-1">*</span>
            </Label>
            <Textarea
              {...register('justification')}
              placeholder="Enter your internal note"
              rows={2}
            />
            {errors.justification && (
              <p className="text-sm text-red-500">
                {errors.justification.message}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Macros</Label>
            <Select
              value={selectedMacro}
              onValueChange={(value) => {
                setSelectedMacro(value)
                const macro = macros.find((macro) => macro.title == value)
                if (macro) setPublicReply(macro)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select macro" />
              </SelectTrigger>
              <SelectContent>
                {macros.map((macro, index) => {
                  return (
                    <SelectItem value={macro.title} key={index}>
                      {macro.title}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
            {errors.reasoning && (
              <p className="text-sm text-red-500">{errors.reasoning.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Public Reply</Label>
            <Textarea
              {...register('public_reply')}
              placeholder="Enter your reply"
              rows={6}
            />
          </div>

          {submitFlagStatus === 'error' && (
            <div className="text-sm text-gray-600">
              <InfoCircleIcon className="text-green-600" /> There was an error
              submitting your flag
            </div>
          )}
          <div className="flex gap-2">
            <SubmitButton status={submitFlagStatus} className="" />
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default PaymentReviewPopup
